"use client";

import React from "react";
import { FileText, Banknote, CalendarCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "01",
  title: "Submit Your Property",
  description: "Provide basic details about your house. There is no obligation, and the process is completely free and confidential.",
  icon: FileText,
  delay: "delay-0",
  marginTop: "mt-0"
}, {
  num: "02",
  title: "Receive Cash Offer",
  description: "We evaluate your property and present a fair, transparent cash offer with no hidden fees or realtor commissions.",
  icon: Banknote,
  delay: "delay-150",
  marginTop: "md:mt-16"
}, {
  num: "03",
  title: "Close & Get Paid",
  description: "You choose the closing date. We cover all closing costs, and you walk away with cash in as little as 7–14 days.",
  icon: CalendarCheck,
  delay: "delay-300",
  marginTop: "md:mt-32"
}];
export default function Our3StepSimpleProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-3-step-simple-process" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-2xl mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
            How It Works
          </h2>
          <p className="text-lg text-muted-foreground">
            Selling your house shouldn't be complicated. Our streamlined process is designed to get you a fair cash offer and close on your timeline.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, i) => <StepCard key={i} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = step.icon;
  return <div data-index={index} ref={ref} className={`transition-all duration-700 ease-out ${step.marginTop} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${step.delay}`}>
      <div className="group relative h-full">
        {}
        <div className="absolute -inset-0.5 bg-primary/20 blur-2xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <Card className="relative h-full bg-card/40 backdrop-blur-md border-border/50 hover:border-primary/30 transition-colors duration-300 overflow-hidden">
          <CardHeader className="pb-4 relative z-10">
            <div className="flex justify-between items-start mb-6">
              <div className="p-3 rounded-lg bg-secondary/50 text-primary ring-1 ring-border/50 group-hover:bg-primary/10 group-hover:ring-primary/30 transition-all duration-300">
                <Icon className="w-8 h-8 stroke-[1.5]" />
              </div>
              <span className="font-mono text-5xl lg:text-6xl font-black text-primary/80 group-hover:text-primary transition-colors duration-300 select-none">
                {step.num}
              </span>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground tracking-tight">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <CardDescription className="text-base text-muted-foreground leading-relaxed">
              {step.description}
            </CardDescription>
          </CardContent>
          
          {}
          <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
        </Card>
      </div>
    </div>;
}